/*  eslint-disable no-unused-vars, react/no-unescaped-entities */
import React, { useState } from 'react';
import { Navbar } from '../../components';
import './LexiLensAI.css';

const LexiLensAI = () => {
  const [playing, setPlaying] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqData = [
    {
      q: 'How is LexiLensAI different from LangSmith, Arize, or Langfuse?',
      a: 'Those platforms extend distributed tracing and APM tooling to cover LLM calls. LexiLensAI is built session-first — the Session is the primary operational object, not the trace or span. This means execution lineage (causality), context window evolution, memory transitions, and instruction drift are first-class citizens. When a governance violation occurs, LexiLensAI can identify which context compaction removed the policy instruction that caused it — something no trace-centric tool surfaces today.',
    },
    {
      q: 'How does LexiLensAI prevent runaway AI agent costs?',
      a: 'LexiLensAI continuously monitors execution paths, token consumption patterns, recursive tool-calling behavior, and abnormal workflow expansion across active AI agents. Runtime safeguards can automatically detect and halt runaway execution loops before cloud costs escalate or production systems are impacted.',
    },
    {
      q: 'Does LexiLensAI support native session-log ingestion for Claude-based agents?',
      a: 'Yes. In addition to OpenTelemetry trace reconstruction, LexiLensAI supports native session-log ingestion for agents built on the Claude Agent SDK and Managed Agents. Where a harness already maintains a durable, structured session event log, LexiLensAI can ingest that log directly — preserving higher-fidelity signals such as profile switches and context transitions without the overhead of trace-to-session reconstruction.',
    },
    {
      q: 'Why does LexiLensAI focus on sessions instead of individual traces?',
      a: 'Enterprise AI systems often span multiple agents, tools, models, and context windows over extended periods of time. Individual traces explain isolated events, while sessions capture the complete operational history. LexiLensAI reconstructs sessions to help teams understand what happened, why it happened, what changed over time, and how outcomes were produced.',
    },
    {
      q: 'Can LexiLensAI trace multi-agent workflows across MCP tool chains?',
      a: 'Yes. LexiLensAI traces distributed multi-agent workflows across MCP tool chains, asynchronous agent delegations, and external tool invocations. The platform rebuilds parent-child execution graphs to help teams understand how complex agent workflows evolve in production environments.',
    },
    {
      q: 'Does LexiLensAI support multiple model providers?',
      a: 'Yes. LexiLensAI is designed as a vendor-neutral runtime intelligence layer that can operate across managed foundation models, self-hosted inference platforms, and hybrid AI environments. This allows platform teams to maintain consistent observability, governance, and operational visibility across heterogeneous AI ecosystems.',
    },
    {
      q: 'Does LexiLensAI replace existing guardrail or policy frameworks?',
      a: 'No. LexiLensAI is designed to integrate with enterprise runtime governance and guardrail systems while providing unified observability, execution tracing, operational analytics, and runtime intelligence across distributed AI agent workflows. The platform can ingest telemetry and lifecycle events from orchestration frameworks, policy engines, and OpenTelemetry pipelines to help platform teams analyze, debug, and govern complex AI systems in production.',
    },
    {
      q: 'How does LexiLensAI integrate with existing AI platforms?',
      a: 'LexiLensAI is designed to integrate with modern AI architectures through OpenTelemetry-compatible instrumentation, orchestration framework integrations, and runtime telemetry pipelines. The platform can observe agent workflows, tool interactions, model invocations, and infrastructure events without requiring organizations to replace their existing AI stack.',
    },
    {
      q: 'Can LexiLensAI explain why an agent made a particular decision?',
      a: 'LexiLensAI correlates execution lineage, tool interactions, session history, and runtime context to help platform teams understand how outcomes were produced. By analyzing execution sessions, teams can investigate decision paths, replay workflows, and perform operational root-cause analysis.',
    },
  ];

  return (
    <div className="lexilensai-page">
      <Navbar />

      {/* HERO */}
      <section className="hero">
        <div className="hero-content">
          <h1>LexiLensAI</h1>
          <h2>
            Session-Native Runtime Intelligence and Auditability for Enterprise AI Agents.
          </h2>
          <p className="hero-sub">
            LexiLensAI treats the complete execution session — not the
            individual trace — as the primary operational unit. Reconstruct
            agent delegation chains, detect runaway behaviors, audit decision
            lineage, and govern autonomous workflows across AWS Bedrock and
            private deployments.
          </p>          {/*
          <h2>Performance Intelligence & Deep Observability for Enterprise Agentic Systems.</h2>
          <p className="hero-sub">
            Monitor multi-agent execution graphs, detect runaway autonomous behaviors,
            and optimize agent reliability across AWS Bedrock and private deployments.
            Trace complex multi-agent reasoning
            chains, optimize underlying inference infrastructure, and enforce
            runtime guardrails.
          </p>
          */}
          <div className="hero-cta-group">
            <a href="#contact" className="btn primary">Request Architecture Deep-Dive</a>
          </div>
        </div>
      </section>

      <section className="section demo-showcase">
        <div className="container">

          <h2>See LexiLensAI In Action</h2>

          <p className="section-intro">
            Runtime intelligence for enterprise AI systems —
            combining session analysis, execution tracing,
            governance analysis, anomaly detection,
            replay, and explainable root cause reasoning.
          </p>

          <p className="section-intro">
            LexiLensAI treats long-running sessions — not individual traces —
            as the primary operational unit for understanding,
            auditing, replaying and governing agent behavior.
          </p>

          <div className="card-grid">
            <div className="card">
              <h3>Scenario 1: Healthy Multi-Agent Workflow</h3>

              <p>
                Reconstruct a complete agent session,
                including delegation chains,
                tool interactions,
                execution timelines,
                audit history,
                replay capabilities,
                and cost attribution across the workflow lifecycle.
              </p>

              <div className="scenario-actions">

                <span className="scenario-badge healthy">
                  HEALTHY
                </span>

                <a
                  href="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexiLensAI/LexiLensAI_demo.mp4#t=214"
                  target="_blank"
                  rel="noreferrer"
                  className="btn primary"
                >
                  Watch Demo
                </a>

              </div>

            </div>

            <div className="card">
              <h3>Scenario 2: Recursive Agent Loop</h3>

              <p>
                Detect recursive agent behavior,
                understanding the decision path,
                identify root causes,
                and understand operational impact through
                governance findings and root cause analysis.
              </p>

              <div className="scenario-actions">

                <span className="scenario-badge anomaly">
                  ANOMALY
                </span>

                <a
                  href="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexiLensAI/LexiLensAI_demo.mp4#t=356"
                  target="_blank"
                  rel="noreferrer"
                  className="btn primary"
                >
                  Watch Demo
                </a>

              </div>

            </div>

            <div className="card">
              <h3>Scenario 3: Token Explosion</h3>

              <p>
                Identify token amplification,
                track cost growth across the session,
                surface high-impact execution paths,
                and estimate blast radius before budgets are exceeded.
              </p>

              <div className="scenario-actions">

                <span className="scenario-badge anomaly">
                  ANOMALY
                </span>

                <a
                  href="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexiLensAI/LexiLensAI_demo.mp4#t=420"
                  target="_blank"
                  rel="noreferrer"
                  className="btn primary"
                >
                  Watch Demo
                </a>

              </div>

            </div>
          </div>
          <p className="demo-footnote">
            Demonstrations generated from real multi-agent execution sessions
            and analyzed using OpenTelemetry-compatible telemetry pipelines.
          </p>
        </div>
      </section>

      {/* CATEGORY ANCHOR */}
      <section className="section problem-statement">
        <div className="container narrow">
          <h2>AI Agents Are Becoming Operational Black Boxes</h2>

          <p>
            Enterprises deploying autonomous or long-running AI agents increasingly struggle with runaway execution loops, invisible tool failures, unpredictable token costs, and non-deterministic reasoning behavior.
          </p>

          <p>
            LexiLensAI gives platform teams runtime visibility, execution tracing and operational guardrails for complex multi-agent systems running on AWS Bedrock and modern agent orchestration frameworks.
          </p>
        </div>
      </section>

      {/* WHY NOT EXISTING TOOLS */}
      <section className="section">
        <div className="container narrow">
          <h2>Built Session-First. Not Trace-Extended.</h2>
          <p>
            Most observability platforms were designed for request-response
            tracing and later extended to handle agents. LexiLensAI is
            architected differently — the <strong>Session</strong> is the
            primary operational unit, not the trace.
          </p>

          <div className="comparison-grid">
            <div className="comparison-col">
              <h3 className="comparison-label trace-label">Trace-Centric Tools</h3>
              <ul className="comparison-list">
                <li>Individual spans and requests</li>
                <li>Timeline: <em>when</em> something happened</li>
                <li>Extended to agents as an afterthought</li>
                <li>No context window evolution modeling</li>
                <li>No memory transition tracking</li>
                <li>No causal lineage across a session</li>
              </ul>
            </div>

            <div className="comparison-col highlight-col">
              <h3 className="comparison-label session-label">LexiLensAI</h3>
              <ul className="comparison-list">
                <li>Complete execution sessions as the primary object</li>
                <li>Lineage: <em>why</em> an outcome was produced</li>
                <li>Built for multi-agent workflows from the ground up</li>
                <li>Context window snapshots and instruction drift detection</li>
                <li>Memory evolution timeline across the session</li>
                <li>Causal graph from user intent to final decision</li>
              </ul>
            </div>
          </div>

          <p className="differentiator-callout">
            Timeline answers: <strong>When?</strong>&nbsp;&nbsp;
            Lineage answers: <strong>Why?</strong>
          </p>
        </div>
      </section>

      <section className="section alt">

        <div className="container">

          <h2>Runtime Failure Scenarios Detected</h2>

          <div className="card-grid">

            <div className="card">
              <h3>Recursive Agent Loop</h3>
              <p>
                Detect cyclic agent delegation patterns before costs escalate.
              </p>
            </div>

            <div className="card">
              <h3>Token Explosion</h3>
              <p>
                Identify exponential token growth and prompt amplification.
              </p>
            </div>

            <div className="card">
              <h3>Retry Storm</h3>
              <p>
                Detect excessive retry behavior causing latency amplification.
              </p>
            </div>

            <div className="card">
              <h3>Tool Failure Cascade</h3>
              <p>
                Trace downstream failures back to the originating tool.
              </p>
            </div>

            <div className="card">
              <h3>Session Drift</h3>
              <p>
                Detect workflows whose behavior diverges from
                expected objectives, policies, or prior session state.
              </p>
            </div>

            <div className="card">
              <h3>Context Loss</h3>
              <p>
                Identify when critical context, memory, or
                instructions are discarded, leading to degraded decisions.
              </p>
            </div>

          </div>

        </div>

      </section>

      {/* WHY LEXILENSAI */}
      <section className="section alt">
        <div className="container narrow">
          <h2>Why Enterprises Need Runtime Intelligence for AI Agents</h2>
          <p>
            As enterprises move from simple chat interfaces to autonomous AI agents,
            operational visibility becomes dramatically harder. Multi-agent systems
            introduce recursive workflows, asynchronous tool execution, non-deterministic
            reasoning paths, and unpredictable infrastructure costs.
          </p>
          <p>
            Traditional observability platforms were designed for services,
            APIs, and infrastructure—not autonomous reasoning systems.
            Platform teams increasingly need to understand why agents made decisions,
            how sessions evolved over time, what context influenced outcomes,
            and where operational or governance risks emerged.
          </p>
          <p>
            Modern AI stacks now span foundation models, orchestration frameworks,
            retrieval systems, MCP tool chains, memory layers, and runtime policy engines.
            LexiLensAI provides a unified runtime intelligence layer that helps enterprises
            observe, reconstruct, replay, govern, and optimize complex AI systems in production.
          </p>
          <p>
            Built on OpenTelemetry-compatible telemetry, LexiLensAI analyzes
            agent sessions and execution lineage, tracks tool lifecycles,
            detects runtime anomalies, enforces governance guardrails,
            enables replay and auditability, and delivers operational cost attribution
            across AWS Bedrock and model-agnostic AI environments.
          </p>
        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="section">
        <div className="container narrow">
          <h2>Agent Sessions Become Operational Assets</h2>
          <p>
            Long-running AI systems create execution histories that span
            multiple agents, tool chains, context windows, memory layers,
            and infrastructure boundaries. LexiLensAI reconstructs these
            execution sessions into navigable timelines, enabling teams
            to audit, replay, analyze, and govern autonomous workflows
            after deployment.
          </p>
          <p>
            As agent architectures increasingly rely on external memory,
            MCP tool ecosystems, workflow engines, and code execution
            runtimes, understanding a single trace becomes insufficient.
            Operational visibility requires understanding the complete
            execution session.
          </p>

          <pre className="session-diagram">{`
            Session
            ├── Agent Delegation
            │   ├── Tool Invocation
            │   └── Knowledge Search
            │
            ├── Research Agent
            │   ├── Model Interaction
            │   └── Memory Update
            │
            ├── Recommendation Agent
            │   ├── Model Interaction
            │   └── Context Transition
            │
            ├── Review Agent
            │   └── Final Decision
            │
            └── Execution Timeline
                ├── Cost Attribution
                └── Audit Record
          `}
          </pre>
          <p className="session-caption">
            LexiLensAI reconstructs complete execution sessions, transforming
            fragmented traces, tool interactions, memory transitions, and
            runtime events into replayable operational history.
          </p>

        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="section">
        <div className="container narrow">
          <h2>Who LexiLensAI Is Built For</h2>
          <ul className="audience-list">
            <li>
              <strong>Platform Engineering Teams:</strong> Tasked with scaling AI agent infrastructure reliably across AWS environments without runaway costs.
            </li>
            <li>
              <strong>AI Ops & SREs:</strong> Engineers needing deep distributed traces to debug non-deterministic agent loops, state drift, and tool-calling failures.
            </li>
            <li>
              <strong>Enterprise Architects:</strong> Leadership requiring model-agnostic governance, strict data privacy, and precise compute spend attribution.
            </li>
          </ul>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section">
        <div className="container">
          <h2>What Platform Teams Gain</h2>
          <div className="card-grid">

            <div className="card">
              <h3>Session Reconstruction & Auditability</h3>
              <p>
                Reconstruct long-running agent sessions across models,
                tools, and workflows. Understand how decisions evolved,
                what context influenced outcomes, and maintain
                replayable audit trails.
              </p>
            </div>

            <div className="card">
              <h3>Runtime Intelligence</h3>
              <p>
                Correlate agent execution latency, token consumption,
                tool interactions, and model-routing decisions to
                identify bottlenecks and operational inefficiencies.
              </p>
            </div>

            <div className="card">
              <h3>Governance & Root Cause Analysis</h3>
              <p>
                Detect recursive agent behavior, tool failure cascades,
                retry storms, and policy violations. Apply runtime
                controls before issues impact users or cloud budgets.
              </p>
            </div>

            <div className="card">
              <h3>Cost Attribution & Optimization</h3>
              <p>
                Track execution costs across agents, sessions,
                workflows, teams, and environments. Identify
                expensive execution paths and optimize model
                utilization at scale.
              </p>
            </div>

            <div className="card">
              <h3>Execution Lineage — Not Just Timeline</h3>
              <p>
                Reconstruct the complete causal chain of decisions, delegations,
                tool interactions, memory transitions, and model invocations
                that produced a final outcome. Timeline tells you <em>when</em>.
                Lineage tells you <em>why</em> — tracing causality from the
                original user intent to the final agent decision.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section alt">
        <div className="container">
          <h2>How LexiLensAI Works</h2>
          <div className="steps">

            <div className="step">
              <span className="step-number">1</span>
              <h3>Capture Sessions & Telemetry</h3>
              <p>
                LexiLensAI ingests OpenTelemetry traces, agent events,
                tool invocations, memory updates, and execution metadata
                to reconstruct complete agent sessions across distributed
                workflows.
              </p>
            </div>

            <div className="step">
              <span className="step-number">2</span>
              <h3>Reconstruct Runtime Intelligence</h3>
              <p>
                LexiLensAI correlates agent decisions, reasoning paths,
                execution lineage, session evolution, token consumption,
                and tool activity to explain how workflows behaved over time.
              </p>
            </div>
            {/*
            <div className="step">
              <span className="step-number">2</span>
              <h3>Correlate Compute Context</h3>
              <p>
                LexiLensAI matches high-level reasoning steps with low-level cluster performance indexes, parsing memory limits, concurrent load demands, and active quantization parameters.
              </p>
            </div>
            */}

            <div className="step">
              <span className="step-number">3</span>
              <h3>Detect, Govern & Explain</h3>
              <p>
                Runtime policies identify anomalous behavior, governance
                violations, and runaway execution patterns. Session replay,
                Root cause analysis & Explainability, and guardrails help
                operators understand and safely remediate failures.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section dashboard-preview">

        <div className="container">

          <h2>Runtime Intelligence Dashboard</h2>

          <p className="section-intro">
            Interactive session reconstruction, execution graphs,
            timeline replay, auditability, anomaly detection,
            governance findings, and explainable root cause analysis.
          </p>

          <div className="card-grid">

            <div className="card">

              <img
                src="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexiLensAI/LexiLensAI_ExecutionGraph.png"
                alt="Runtime Graph"
                className="dashboard-image"
              />

              <h3>Execution Graph</h3>

              <p>
                Reconstruct agent execution lineage,
                tool interactions,
                and workflow dependencies across a complete session.
              </p>

            </div>

            <div className="card">

              <img
                src="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexiLensAI/LexiLensAI_TimelineReplay.png"
                alt="Timeline Replay"
                className="dashboard-image"
              />

              <h3>Timeline Replay</h3>

              <p>
                Replay session execution chronologically
                across agents, tools, decisions, and runtime events.
              </p>

            </div>

            <div className="card">

              <img
                src="https://curvsort-profiles.s3.ap-south-1.amazonaws.com/public/LexiLensAI/LexiLensAI_AnomalyDetection.png"
                alt="Anomaly Detection"
                className="dashboard-image"
              />

              <h3>Anomaly Detection</h3>

              <p>
                Detect recursive loops,
                token amplification,
                governance violations,
                and cost anomalies before they impact production systems.
              </p>

            </div>

          </div>

        </div>

      </section>

      {/* ARCHITECTURE FLOW */}
      <section className="section architecture-section">
        <div className="container">

          <h2>Where LexiLensAI Sits In The Agent Stack</h2>

          <p className="section-intro">
            LexiLensAI operates as a cross-cutting runtime intelligence layer,
            observing agent execution, session evolution, model interactions,
            and infrastructure behavior across enterprise AI systems.
          </p>

          <div className="agent-stack-main">
            <div className="stack-layer">
              <span className="layer-label">Applications</span>
              <p>Enterprise copilots, autonomous workflows, AI-powered platforms</p>
            </div>

            <div className="stack-connector" />

            <div className="stack-layer">
              <span className="layer-label">Agent Frameworks</span>
              <p>LangChain, AutoGen, MCP tool chains, custom orchestration systems</p>
            </div>

            <div className="stack-connector" />

            <div className="stack-layer">
              <span className="layer-label">Execution Runtime</span>
              <p>Code / Sandbox</p>
            </div>

            <div className="stack-connector" />

            <div className="stack-layer">
              <span className="layer-label">Session Layer</span>
              <p>Persistent execution history, event streams, workflow replay, audit records</p>
            </div>

            <div className="stack-connector" />

            <div className="stack-layer">
              <span className="layer-label">Foundation Models</span>
              <p>AWS Bedrock, Claude, OpenAI, open-source inference environments</p>
            </div>

            <div className="stack-connector" />

            <div className="stack-layer">
              <span className="layer-label">Infrastructure</span>
              <p>AWS, EKS, GPUs, vector systems, storage, networking</p>
            </div>

          </div>

          <div className="lexilens-overlay">
            <h3>LexiLensAI Runtime Intelligence Layer</h3>
            <div className="overlay-features">
              <span>Distributed Tracing</span>
              <span>Session Replay</span>
              <span>Governance</span>
              <span>Cost Attribution</span>
              <span>Anomaly Detection</span>
              <span>Root Cause Analysis</span>
            </div>
          </div>

        </div>
      </section>

      <section className="section">

        <div className="container">

          <h2>Current Platform Capabilities</h2>

          <div className="card-grid">

            <div className="card">

              <h3>Runtime Intelligence</h3>

              <ul className="audience-list">

                <li>✓ Runtime Graph Reconstruction & Visualization</li>

                <li>✓ Execution Timeline Replay</li>

                <li>✓ Auditability</li>

                <li>✓ Cost Attribution & Budget Enforcement</li>

                <li>✓ Token Accounting & Consumption Analytics</li>

                <li>✓ WHY Reasoning Generation & Root Cause Analysis</li>

              </ul>

            </div>

            <div className="card">

              <h3>Governance & Anomaly Detection</h3>

              <ul className="audience-list">

                <li>✓ Recursive Loop Detection</li>

                <li>✓ Token Explosion Detection</li>

                <li>✓ Failure Cascade Analysis</li>

                <li>✓ Runtime Governance Findings</li>

                <li>✓ Human Escalation Detection</li>

                {/* for CodeExecution leveraged by Agents to save context and tokens */}

                <li>✓ Sandbox Abuse</li>

                <li>✓ Excessive Duration in Code Execution Sandbox</li>

              </ul>

            </div>

            <div className="card">

              <h3>Platform Integration</h3>

              <ul className="audience-list">

                <li>✓ OpenTelemetry Trace Ingestion</li>

                <li>✓ Trace Normalization</li>

                <li>✓ Multi-Agent Workflow Support</li>

                <li>✓ MCP Tool Invocation Tracking</li>

                <li>✓ AWS Bedrock-Compatible Architecture</li>

                <li>✓ Native Session-Log Ingestion (Claude Agent SDK / Managed Agents)</li>

              </ul>

            </div>

            <div className="card">

              <h3>Upcoming Capabilities</h3>

              <ul className="audience-list">

                <li>✓ Agent Memory Evolution</li>

                <li>✓ Context Window Transition Visibility</li>

                <li>✓ Context Compaction Analysis</li>

                <li>✓ Session Replay Across Context Resets</li>

              </ul>

            </div>

          </div>

        </div>

      </section>

      {/* RUNTIME INTELLIGENCE LOOP */}
      <section className="section">
        <div className="container">

          <h2>The Runtime Intelligence Loop</h2>

          <div className="card-grid">

            <div className="card">
              <h3>Observe</h3>

              <p>
                Capture telemetry across agents, tools,
                models, sessions, and infrastructure using
                OpenTelemetry-compatible instrumentation.
              </p>
            </div>

            <div className="card">
              <h3>Reconstruct</h3>

              <p>
                Rebuild complete execution sessions,
                including agent delegations, tool interactions,
                reasoning paths, state transitions,
                and workflow timelines.
              </p>
            </div>

            <div className="card">
              <h3>Explain</h3>

              <p>
                Understand why outcomes occurred through
                execution lineage, anomaly analysis,
                governance findings, and session replay.
              </p>
            </div>

            <div className="card">
              <h3>Govern</h3>

              <p>
                Enforce runtime policies, detect anomalous
                behavior, control costs, and improve
                reliability across production AI systems.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* MODEL AGNOSTIC ARCHITECTURE */}
      <section className="section alt">
        <div className="container narrow">

          <h2>Built For Multi-Model AI Ecosystems</h2>

          <p>
            Modern AI platforms span multiple foundation models,
            orchestration frameworks, retrieval systems,
            MCP tool chains, and infrastructure environments.
          </p>

          <p>
            LexiLensAI operates independently of model vendors,
            providing a unified runtime intelligence layer across
            AWS Bedrock, Anthropic ecosystems, OpenAI deployments,
            self-hosted models, and hybrid AI infrastructures.
          </p>

          <p>
            This enables consistent observability, governance,
            session reconstruction, replay, and cost attribution
            across heterogeneous enterprise AI environments.
          </p>

        </div>
      </section>

      {/* DEPLOYMENT USE CASES */}
      <section className="section alt">
        <div className="container">

          <h2>LexiLensAI Deployment Use Cases</h2>

          <p className="section-intro">
            Runtime intelligence for enterprise AI systems operating across
            multiple agents, tools, models, and infrastructure environments.
          </p>

          <div className="demo-block">
            <h3>Enterprise Agent Operations</h3>

            <p>
              Observe, reconstruct, and govern long-running agent sessions
              across customer support, research automation, workflow
              orchestration, and enterprise copilots.
            </p>
          </div>

          <div className="demo-block">
            <h3>Platform Engineering & Reliability</h3>

            <p>
              Identify execution bottlenecks, investigate anomalous behavior,
              analyze workflow failures, and improve reliability across
              production AI environments.
            </p>
          </div>

          <div className="demo-block">
            <h3>Governance, Auditability & FinOps</h3>

            <p>
              Enforce operational policies, reconstruct decision paths,
              audit agent behavior, attribute costs, and maintain
              visibility across regulated environments.
            </p>
          </div>

        </div>
      </section>

      {/* PRIVACY */}
      <section className="section alt">
        <div className="container narrow">

          <h2>Designed For Enterprise Security & Data Isolation</h2>

          <p>
            Enterprise AI systems frequently operate on sensitive business,
            customer, and operational data. Security, privacy, and deployment
            flexibility are foundational design requirements for LexiLensAI.
          </p>

          <p>
            LexiLensAI is designed to minimize data exposure while providing
            runtime intelligence across agent workflows, tool interactions,
            model invocations, and infrastructure events.
          </p>

          <p>
            Flexible deployment options—including private cloud, VPC, and
            self-managed environments—allow organizations to maintain
            control over their operational telemetry and governance policies.
          </p>

        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="section faq-section">
        <div className="container narrow">
          <h2>Technical Architecture & FAQ</h2>
          <div className="faq-accordion">
            {faqData.map((item, index) => (
              <div key={index} className={`faq-item ${activeFaq === index ? 'active' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  <h3>{item.q}</h3>
                  <span className="faq-icon">{activeFaq === index ? '−' : '+'}</span>
                </div>
                {activeFaq === index && (
                  <div className="faq-answer">
                    <p>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="cta">

        <h2>Move Beyond Traces. Understand The Entire Session.</h2>

        <h3>
          Runtime intelligence for enterprise AI systems.
        </h3>

        <p>
          Reconstruct execution sessions, investigate outcomes,
          govern agent behavior, and understand why decisions were made.
        </p>

        <a
          href="mailto:rajeev.bakshi@curvsort.com?subject=LexiLensAI%20Enterprise%20Pilot%20Inquiry"
          className="btn primary"
        >
          Request Enterprise Access
        </a>

      </section>

    </div>
  );
};

export default LexiLensAI;
